import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowsClockwise, MagnifyingGlass, SlidersHorizontal } from 'src/common/icons';
import { Button, Checkbox, Input, Pager, PageTitle, Table } from 'src/components';
import messages from './messages';

import KeysModal from './components/KeysModal';
import GroupsModal from './components/GroupsModal';

import styles from './styles.module.scss';
import { useEffect, useMemo, useReducer, useState } from 'react';
import AddUserModal from './components/AddUserModal';
import { useUserManagementQuery } from 'src/api/queries/userManagementQueries';
import { useSubscriptionQuery } from 'src/api/queries/subscriptionQueries';
import LoadingScreen from './components/LoadingScreen';
import SvgKey from 'src/common/icons/Key';
import SvgGear from 'src/common/icons/Gear';
import UserSettingsModal from './components/UserSettingsModal/UserSettingsModal';
import ResendSignupButton from './components/ResendSignupButton';
import RemoveMemberModal from './components/RemoveMemberModal';
import ReassignInviteModal from './components/ReassignInviteModal';
import RestoreMemberButton from './components/RestoreMemberButton';
import { motion } from 'framer-motion';
import { useDebounce } from 'use-debounce';

import useModal from 'src/hooks/useModal';
import { useAuthUser } from 'react-auth-kit';
import BoardingTour from 'src/components/BoardingTour';
import { useConnectedAppsQuery } from 'src/api/queries/connectedAppsQueries';
import { ConnectedAppsModal, messages as connectedAppsMessages } from 'src/components/ConnectedApps';
import connectedAppsStyles from 'src/components/ConnectedApps/ConnectedApps.module.scss';

const initialFilters = {
  page: 1,
  limit_per_page: 30,
  email: '',
  first_name: '',
  last_name: '',
  enabled: false,
  include_removed: false,
};

// `user.invite_status` of a member removed from the team (plan-structure v2
// Phase 7a). The value is spelled DISABLED for historical reasons — it is the
// one state that holds NO seat and cannot log in; the row survives so the
// person stays the author of everything they uploaded.
const REMOVED_INVITE_STATUS = 'DISABLED';

// Roles that consume a CONTRIBUTOR seat; the read-only USER role draws on the
// separate (much larger) viewer allowance. Mirrors CONTRIBUTOR_ROLE_IDS /
// VIEWER_ROLE_IDS in filespin/core/subscription.py.
const CONTRIBUTOR_ROLE_IDS = [1, 2, 4];

const userDetailsFromLocalStorage = 'fs.auth._state'

const filtersReducer = (state: any, payload: { [key: string]: any }) => ({ ...state, ...payload });

// Stable across renders: the query is keyed on it.
const ACCOUNT_SCOPE = { kind: 'account' } as const;

const Users = () => {
  const auth = useAuthUser();
  const authenticatedUser = auth();
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);

  const userId=JSON.parse(localStorage.getItem(userDetailsFromLocalStorage) || '{}').userId

  const [emailSearch, setEmailSearch] = useState('');
  const [firstNameSearch, setFirstNameSearch] = useState('');
  const [lastNameSearch, setLastNameSearch] = useState('');
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);

  const [filters, dispatchFilter] = useReducer(filtersReducer, initialFilters);

  const [emailSearchDebounced] = useDebounce(emailSearch, 500);
  const [firstNameSearchDebounced] = useDebounce(firstNameSearch, 500);
  const [lastNameSearchDebounced] = useDebounce(lastNameSearch, 500);
  const [enabledOnlyDebounced] = useDebounce(enabledOnly, 500);

  const { showModal, closeModal } = useModal();

  const [totalPage, setTotalPage] = useState(1);

  const { formatMessage } = useIntl();

  const { data, isLoading, refetch, isRefetching } = useUserManagementQuery(filters, {
    onSuccess: (results) => {
      setTotalPage(Math.ceil(results.total_users / filters.limit_per_page));
    },
  });




  const handleShowKeysModal = (user: any) => showModal(<KeysModal user={user} />);

  // Connected apps (OAuth2 sessions, ui#977): one account-wide request feeds
  // both the per-row counts and the header button, rather than one request
  // per row. A non-admin gets a 403, and then the column and button hide.
  const { data: connectedApps, isError: connectedAppsUnavailable, refetch: refetchConnectedApps } =
    useConnectedAppsQuery(ACCOUNT_SCOPE);
  const connectedAppsByUser = useMemo(() => {
    const counts = new Map<number, number>();
    (connectedApps?.data ?? []).forEach((app) => counts.set(app.user_id, (counts.get(app.user_id) ?? 0) + 1));
    return counts;
  }, [connectedApps]);
  const showConnectedApps = !!connectedApps && !connectedAppsUnavailable;

  const handleShowConnectedAppsModal = (user?: any) =>
    showModal(<ConnectedAppsModal user={user ? { id: user.id, email: user.email } : undefined} />);

  // Adding/editing a member changes the seat counts, so refresh BOTH the list
  // and the subscription (which carries the seat split behind a 5-min
  // staleTime — without this the "N of M seats" subtitle stays stale).
  const handleMembersChanged = () => {
    refetch();
    refetchSubscription();
  };

  // `contributorSeatsLeft` is computed further down (undefined = unknown,
  // Infinity = unlimited). This closure reads it at CLICK time, so the modal
  // always opens with the current count rather than a stale render's.
  const handleShowAddUserModal = () => showModal(
    <AddUserModal
      onAddUser={handleMembersChanged}
      contributorSeatsLeft={contributorSeatsLeft}
    />,
  );

  const handleShowUserSettingsModal = (user: any) => showModal(<UserSettingsModal user={user} onEditUser={handleMembersChanged} />);

  const handleShowRemoveModal = (user: any) =>
    showModal(<RemoveMemberModal user={user} onRemoved={handleMembersChanged} />);

  const handleShowReassignModal = (user: any) =>
    showModal(<ReassignInviteModal user={user} onReassigned={handleMembersChanged} />);

  const handleToggleFilter = () => {
    setShowFilterSidebar((state) => !state);
  };

  const handleRefetch = () => {
    refetch();
    refetchConnectedApps();
  };

  useEffect(() => {
    dispatchFilter({ email: emailSearchDebounced, page: 1 });
  }, [emailSearchDebounced]);

  useEffect(() => {
    dispatchFilter({ first_name: firstNameSearchDebounced, page: 1 });
  }, [firstNameSearchDebounced]);

  useEffect(() => {
    dispatchFilter({ last_name: lastNameSearchDebounced, page: 1 });
  }, [lastNameSearchDebounced]);

  useEffect(() => {
    dispatchFilter({ enabled: enabledOnlyDebounced, page: 1 });
  }, [enabledOnlyDebounced]);

  useEffect(() => {
    dispatchFilter({ include_removed: showRemoved, page: 1 });
  }, [showRemoved]);

  const user_limit=useMemo(()=>{
    if (data?.users.length>0){
      return data.users.filter((user:any)=>user.id === userId)[0]?.subscription?.details.user_limit ?? 0
    }
  },[data?.users])

  // Seats (plan-structure v2 Phase 6): a plan includes N CONTRIBUTOR seats
  // (ADMIN/MANAGER/CREATOR, + any bought at $9/mo) AND a separate, much larger
  // pool of free VIEWER seats (the read-only USER role). `usage.users` carries
  // both counts. Falls back to the legacy head-count-vs-user_limit rule when
  // the subscription isn't readable (a MANAGER gets 403) or predates Phase 6,
  // so this page never gets MORE permissive than the server gate.
  const { data: subscription, refetch: refetchSubscription } =
    useSubscriptionQuery({ retry: false });
  const seats = subscription?.usage?.users;
  const hasSeatSplit = seats?.contributor_quota !== undefined;

  const contributorSeatsLeft =
    !hasSeatSplit ? undefined
      : seats?.contributor_quota === 0 ? Infinity
      : (seats?.contributor_quota ?? 0) - (seats?.contributors ?? 0);
  const viewerSeatsLeft =
    !hasSeatSplit ? undefined
      : seats?.viewer_quota === 0 ? Infinity
      : (seats?.viewer_quota ?? 0) - (seats?.viewers ?? 0);

  // Any free seat of EITHER class means there is something to invite.
  const canAddUser = hasSeatSplit
    ? (contributorSeatsLeft ?? 0) > 0 || (viewerSeatsLeft ?? 0) > 0
    : !(data?.count >= user_limit);

  /** Is there room to restore a removed member back into their own role class? */
  const seatAvailableForRole = (roleId: number) => {
    if (!hasSeatSplit) return true;   // unknown — let the server decide
    const left = CONTRIBUTOR_ROLE_IDS.includes(Number(roleId))
      ? contributorSeatsLeft : viewerSeatsLeft;
    return (left ?? 0) > 0;
  };

  const seatsSubtitle = useMemo(() => {
    if (!hasSeatSplit) return user_limit ? `( Max ${user_limit} users )` : '';
    const part = (label: string, used = 0, left?: number, quota?: number) =>
      left === Infinity
        ? `${used} ${label}${used === 1 ? '' : 's'} (unlimited)`
        : `${used} of ${quota ?? 0} ${label}${(quota ?? 0) === 1 ? '' : 's'}`;
    return `( ${part('contributor seat', seats?.contributors, contributorSeatsLeft, seats?.contributor_quota)}`
      + ` · ${part('viewer seat', seats?.viewers, viewerSeatsLeft, seats?.viewer_quota)} )`;
  }, [hasSeatSplit, user_limit, seats, contributorSeatsLeft, viewerSeatsLeft]);

  return (
    <><BoardingTour title='Users' description='This page shows the list of Users' path='/admin/users' uniqueId='USERS' />
    <div data-test="user-page-content">
      <PageTitle
        className={styles.pageWrapper}
        title={formatMessage(messages.pageTitle)}

        counter={data?.count}
        subtitle={seatsSubtitle}

        rightContainer={
          <div className={styles.headerButtons}>
            {showConnectedApps && (
              <Button mode="tertiary" size="small" onClick={() => handleShowConnectedAppsModal()}>
                {formatMessage(connectedAppsMessages.title)} ({connectedApps!.data.length})
              </Button>
            )}
            <Button size="small" onClick={handleShowAddUserModal} disabled={!canAddUser}>
              <FormattedMessage {...messages.addUser} />
            </Button>
          </div>
        }
      />
      {/* Multi-group subscriptions only (in practice: Enterprise). This page
          lists ONE group's members, but contributor seats are counted across
          every group on the subscription — without this strip, "5/5 seats but
          I only see 3 people" has no visible explanation. The group list
          itself sits behind the page's own "Click to view" reveal (the KEYS
          pattern) rather than inline — with several groups the prose ran on.
          Single-group accounts (all self-serve) see nothing, and when the
          subscription isn't readable (a MANAGER's 403) `subscription` is
          undefined and the strip hides itself. Moved here from the
          Subscription page, where it was a one-row table for almost every
          account. */}
      {(subscription?.groups?.length ?? 0) > 1 && (
        <div className={styles.groupsStrip}>
          <FormattedMessage
            {...messages.groupsStripIntro}
            values={{ count: subscription!.groups.length }}
          />
          <Button
            mode="secondary"
            size="small"
            onClick={() => showModal(<GroupsModal groups={subscription!.groups} />)}
          >
            <FormattedMessage {...messages.clickToView} />
          </Button>
        </div>
      )}
      <div className={styles.searchSection}>
        <div>
          <Button icon={<SlidersHorizontal />} mode="flat" onClick={handleToggleFilter} />
          <Input
            placeholder={formatMessage(messages.searchPlaceholder)}
            value={emailSearch}
            onChange={setEmailSearch}
            icon={<MagnifyingGlass />}
          />
        </div>

        <Button icon={<ArrowsClockwise />} mode="flat" onClick={handleRefetch} loading={isRefetching} />
      </div>
      <div className={styles.pageContent}>
        <motion.div
          initial={false}
          animate={{
            x: showFilterSidebar ? '0%' : '-100%',
          }}
          className={styles.sidebar}
          transition={{ ease: 'easeInOut', duration: 0.2 }}
        >
          <h4>
            <FormattedMessage {...messages.searchFilter} />
          </h4>

          <Input label={formatMessage(messages.firstName)} value={firstNameSearch} onChange={setFirstNameSearch} />
          <Input
            className={styles.formSpacing}
            label={formatMessage(messages.lastName)}
            value={lastNameSearch}
            onChange={setLastNameSearch}
          />
          <Checkbox
            label={formatMessage(messages.enabledOnly)}
            checked={enabledOnly}
            onChangeCapture={() => setEnabledOnly((state) => !state)}
          />
          {/* Removed members hold no seat and cannot log in — hidden unless
              asked for, so the head-count matches the seat counts above. */}
          <div className={styles.filterSpacing}>
            <Checkbox
              label={formatMessage(messages.showRemoved)}
              checked={showRemoved}
              onChangeCapture={() => setShowRemoved((state) => !state)}
            />
          </div>
        </motion.div>
        <div className={styles.container}>
          {isLoading ? (
            <LoadingScreen message={formatMessage(messages.loadingMessage)} />
          ) : (
            <>
              <Table
                className={styles.centeredTable}
                headers={[
                  'ID',
                  'Email',
                  formatMessage(messages.firstName),
                  formatMessage(messages.lastName),
                  formatMessage(messages.role),
                  formatMessage(messages.keys),
                  ...(showConnectedApps ? [formatMessage(connectedAppsMessages.title)] : []),
                  formatMessage(messages.status),
                  formatMessage(messages.settings),
                ]}
              >
                {data.users.map((user: any) => {
                  const { id, email, first_name, last_name, role_name, enabled, invite_status } = user;
                  const isRequested = invite_status === 'REQUESTED';
                  // Removed members (Phase 7a) hold no seat and cannot log in;
                  // the row survives only so their assets keep their author.
                  const isRemoved = invite_status === REMOVED_INVITE_STATUS;
                  // Neither yourself nor the account owner can be removed —
                  // mirrors the server-side guard, so the action never appears
                  // only to fail.
                  const isManageable = id !== authenticatedUser?.userId
                    && id !== subscription?.owner_user_id;

                  return (
                    <tr key={id} className={isRemoved ? styles.removedRow : undefined}>
                      <td>{id}</td>
                      <td>{email}</td>
                      <td>{first_name}</td>
                      <td>{last_name}</td>
                      <td>{role_name}</td>
                      <td>
                        <Button icon={<SvgKey />} mode="primary" size="small" onClick={() => handleShowKeysModal(user)}>
                          <FormattedMessage {...messages.clickToView} />
                        </Button>
                      </td>
                      {showConnectedApps && (
                        <td>
                          {connectedAppsByUser.get(id) ? (
                            <button
                              className={connectedAppsStyles.countButton}
                              onClick={() => handleShowConnectedAppsModal(user)}
                            >
                              {formatMessage(connectedAppsMessages.appsCount, { count: connectedAppsByUser.get(id) })}
                            </button>
                          ) : (
                            <span className={connectedAppsStyles.countNone}>
                              {formatMessage(connectedAppsMessages.appsCount, { count: 0 })}
                            </span>
                          )}
                        </td>
                      )}
                      <td>
                        {isRemoved ? (
                          <div className={styles.pendingStatusCell}>
                            <span className={styles.removedStatus}>{formatMessage(messages.removed)}</span>
                            <RestoreMemberButton
                              userId={id}
                              email={email}
                              seatAvailable={seatAvailableForRole(user.role_id)}
                              onRestored={handleMembersChanged}
                            />
                          </div>
                        ) : isRequested ? (
                          <div className={styles.pendingStatusCell}>
                            <span className={styles.pendingStatus}>{formatMessage(messages.pendingSignup)}</span>
                            <ResendSignupButton userId={id} email={email} />
                          </div>
                        ) : (
                          formatMessage(enabled ? messages.enabled : messages.disabled)
                        )}
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          <Button
                            icon={<SvgGear />}
                            mode="flat"
                            size="small"
                            onClick={() => handleShowUserSettingsModal(user)}
                            disabled={id === authenticatedUser?.userId}
                          />
                          {isManageable && !isRemoved && (
                            <>
                              {isRequested && (
                                <button
                                  className={styles.resendButton}
                                  onClick={() => handleShowReassignModal(user)}
                                >
                                  {formatMessage(messages.reassignInvite)}
                                </button>
                              )}
                              <button
                                className={styles.removeButton}
                                onClick={() => handleShowRemoveModal(user)}
                              >
                                {formatMessage(isRequested ? messages.revokeInvite : messages.removeMember)}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Table>
              {data?.total_users > 30 && (
                <div className={styles.pagerWrapper}>
                  <Pager
                    currentPage={filters.page}
                    totalPages={totalPage}
                    onChange={(page) => dispatchFilter({ page })}
                    itemsPerPage={30}
                    onItemsPerPageChange={(newLimit) => {
                      return newLimit;
                    }}
                    showItemsPerPage={false}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/*modalKeysUser && <KeysModal user={modalKeysUser} visible={showKeysModal} onClose={handleCloseKeyModal} />}
      <AddUserModal visible={showAddUserModal} onClose={handleCloseAddUserModal} />
      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          visible={showEditUserModal}
          onClose={handleCloseEditUserModal}
          onEditUser={handleEditUser}
        />
      )*/}
    </div>
    </>
  );
};

export default Users;
